import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useNavigateWithNewTab } from '../hooks/useNavigateWithNewTab';
import {
  Plus,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
} from 'lucide-react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  Card,
  CardContent,
  Input,
  Alert,
  AlertDescription,
  DatePicker,
  Checkbox,
} from '@roger/ui-components';
import {
  FileText,
  AlertCircle,
  Loader2,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
} from 'lucide-react';
import {
  useIntake,
  type IntakeFilters,
  type PaginatedIntakesResult,
} from '../hooks/useIntake';
import { DEFAULT_ENABLED_TABS } from '../constants/intakeTabs';
import type { IntakeRecord } from '../store/slices/intakeSlice';

import NewIntakeDialog from '../components/NewIntakeDialog';
import IntakeRowWithStatus from '../components/IntakeRowWithStatus';
import IntakeStatusFilter, {
  type StatusFilterType,
} from '../components/IntakeStatusFilter';
import IntakeAssigneeFilter, {
  type AssigneeFilterValue,
} from '../components/IntakeAssigneeFilter';
import FailedIntakeDrawer from '../components/FailedIntakeDrawer';
import RerunIntakeDialog from '../components/RerunIntakeDialog';
import IntakeSelectionActions from '../components/IntakeSelectionActions';
import { downloadIntakeExport } from '../utils/intakeExport';
import { getUserFacingApiMessageAsync } from '../utils/apiErrorDetail';
import { useToast } from '../hooks/useToastContext';
import { useSetSupportContext } from '../hooks/useSupportContext';
import IntakeManagerColumnsEditorDialog from '../components/IntakeManagerColumnsEditorDialog';
import { useAgencyIntakeManagerColumns } from '../hooks/useAgencyIntakeManagerColumns';
import {
  effectiveIntakeManagerColumns,
  labelForIntakeManagerColumn,
  sortKeyForIntakeManagerColumn,
  type IntakeSortColumn,
} from '../config/intakeManagerColumns';
import { formatDateToISO, parseISOToLocalDate } from '../utils/dateUtils';
import {
  getDefaultIntakeDateRange,
  isIncludeAllSelection,
} from '../utils/intake';
import { getDefaultLandingPath } from '../utils/defaultLandingPath';
import { ClinicSelect } from '../components/clinic/ClinicSelect';

const DEFAULT_PER_PAGE = 20;

/** Map persisted Intake Manager status filters to current shape (default Active). */
function normalizeIntakeManagerStatusFilters(
  stored: string[] | undefined
): StatusFilterType[] {
  const raw = stored ?? [];
  if (raw.length === 0) return ['active'];
  if (raw.length === 1 && raw[0] === 'show_all') return ['active'];
  return raw as StatusFilterType[];
}

type SortOrder = 'asc' | 'desc';

const IntakeManager: React.FC = () => {
  const {
    hasPermission,
    hasAnyPermission,
    hasFeatureFlag,
    updateIntake: updateIntakeInStore,
    agencyInsurancesLoading,
    fetchAgencyInsurances,
    shouldRefreshAgencyInsurances,
    intakeManagerFilters,
    setIntakeManagerFilters,
    agencyClinics,
    agencyClinicsLoading,
    fetchAgencyClinics,
    user,
  } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
      hasAnyPermission: state.hasAnyPermission,
      hasFeatureFlag: state.hasFeatureFlag,
      updateIntake: state.updateIntake,
      agencyInsurancesLoading: state.agencyInsurancesLoading,
      fetchAgencyInsurances: state.fetchAgencyInsurances,
      shouldRefreshAgencyInsurances: state.shouldRefreshAgencyInsurances,
      intakeManagerFilters: state.intakeManagerFilters,
      setIntakeManagerFilters: state.setIntakeManagerFilters,
      agencyClinics: state.agencyClinics,
      agencyClinicsLoading: state.agencyClinicsLoading,
      fetchAgencyClinics: state.fetchAgencyClinics,
      user: state.user,
    }))
  );

  const { error: showError } = useToast();
  const enabledTabs = DEFAULT_ENABLED_TABS;
  const {
    intakes,
    intakesLoading,
    intakesError,
    loadIntakes,
    loadIntakeDetails,
  } = useIntake();

  // Support context
  useSetSupportContext({
    label: 'Intake Manager',
    subject: 'Intake Manager - Support request',
    hiddenContext: {
      ...(intakeManagerFilters?.searchTerm && {
        searchTerm: intakeManagerFilters.searchTerm,
      }),
      ...(intakeManagerFilters?.statusFilters?.length && {
        statusFilters: (intakeManagerFilters.statusFilters as string[]).join(
          ', '
        ),
      }),
    },
  });

  // Filter states - initialize from persisted store
  const [searchTerm, setSearchTerm] = useState(
    intakeManagerFilters?.searchTerm ?? ''
  );
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(
    intakeManagerFilters?.searchTerm ?? ''
  );
  const [statusFilters, setStatusFilters] = useState<StatusFilterType[]>(() =>
    normalizeIntakeManagerStatusFilters(intakeManagerFilters?.statusFilters)
  );
  const [assigneeFilter, setAssigneeFilter] = useState<AssigneeFilterValue>(
    intakeManagerFilters?.assigneeFilter ?? {
      userIds: [],
      includeUnassigned: false,
    }
  );
  const [clinicFilter, setClinicFilter] = useState<string>(
    intakeManagerFilters?.clinicFilter ?? 'all'
  );
  // First-time seed for the pickers: today back six weeks, so the landing view
  // is bounded and the applied range is visible rather than implied. The
  // backend applies no date filter when the params are omitted, so an empty
  // picker really means "all dates".
  // Only the initial state read seeds the pickers. The per-render value is
  // otherwise used just to tell an untouched default range from a user-adjusted
  // one in the empty-state hint, so this does not re-seed a page left open.
  const defaultDateRange = getDefaultIntakeDateRange();
  // `??` (not `||`) so a deliberately cleared date persisted as '' stays empty
  // across sessions; only a first-time user (undefined) gets the seed.
  const [startDate, setStartDate] = useState<string>(
    intakeManagerFilters?.startDate ?? defaultDateRange.startDate
  );
  const [endDate, setEndDate] = useState<string>(
    intakeManagerFilters?.endDate ?? defaultDateRange.endDate
  );

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState<PaginatedIntakesResult>({
    items: [],
    total: 0,
    page: 1,
    perPage: DEFAULT_PER_PAGE,
    totalPages: 1,
  });

  const [showNewIntakeDialog, setShowNewIntakeDialog] = useState(false);
  const navigate = useNavigate();
  const navigateTo = useNavigateWithNewTab();

  // Rerun modal state
  const [rerunModalOpen, setRerunModalOpen] = useState(false);
  const [intakeToRerun, setIntakeToRerun] = useState<IntakeRecord | null>(null);

  // Failed intake drawer state
  const [failedDrawerOpen, setFailedDrawerOpen] = useState(false);
  const [selectedFailedIntake, setSelectedFailedIntake] =
    useState<IntakeRecord | null>(null);

  // Selection state
  const [selectedIntakeIds, setSelectedIntakeIds] = useState<string[]>([]);

  // Export state
  const [isExporting, setIsExporting] = useState(false);

  const [columnsEditorOpen, setColumnsEditorOpen] = useState(false);

  const {
    stored: intakeManagerStoredColumns,
    refresh: refreshIntakeManagerColumns,
  } = useAgencyIntakeManagerColumns();

  const visibleColumns = useMemo(
    () => effectiveIntakeManagerColumns(intakeManagerStoredColumns),
    [intakeManagerStoredColumns]
  );

  const canAccessAdminIntakeActions = useMemo(
    () => hasAnyPermission(['external-admin:access', 'emr:manage']),
    [hasAnyPermission]
  );

  // Sort state: default enqueue time (created_at) descending (newest first)
  const [sortBy, setSortBy] = useState<IntakeSortColumn>(
    (intakeManagerFilters?.sortBy as IntakeSortColumn) ?? 'created_at'
  );
  const [sortOrder, setSortOrder] = useState<SortOrder>(
    (intakeManagerFilters?.sortOrder as SortOrder) ?? 'desc'
  );

  // Track if this is initial mount
  const isInitialMount = useRef(true);
  const hasFetchedAgencyInsurancesRef = React.useRef(false);
  const hasFetchedAgencyClinicsRef = React.useRef(false);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Persist all filters to store when they change
  useEffect(() => {
    setIntakeManagerFilters({
      statusFilters,
      assigneeFilter,
      searchTerm: debouncedSearchTerm,
      clinicFilter,
      startDate,
      endDate,
      sortBy,
      sortOrder,
    });
  }, [
    statusFilters,
    assigneeFilter,
    debouncedSearchTerm,
    clinicFilter,
    startDate,
    endDate,
    sortBy,
    sortOrder,
    setIntakeManagerFilters,
  ]);

  // Check permission - redirect if user doesn't have intake:access
  useEffect(() => {
    if (!hasPermission('intake:access')) {
      navigate(
        getDefaultLandingPath({
          hasPermission,
          hasAnyPermission,
          hasFeatureFlag,
        })
      );
    }
  }, [hasAnyPermission, hasFeatureFlag, hasPermission, navigate]);

  // Fetch agency insurances on component mount (once, globally cached)
  useEffect(() => {
    if (
      !hasFetchedAgencyInsurancesRef.current &&
      shouldRefreshAgencyInsurances() &&
      !agencyInsurancesLoading
    ) {
      hasFetchedAgencyInsurancesRef.current = true;
      fetchAgencyInsurances();
    }
  }, [
    shouldRefreshAgencyInsurances,
    agencyInsurancesLoading,
    fetchAgencyInsurances,
  ]);

  // Fetch agency clinics on component mount
  useEffect(() => {
    if (
      !hasFetchedAgencyClinicsRef.current &&
      agencyClinics.length === 0 &&
      !agencyClinicsLoading
    ) {
      hasFetchedAgencyClinicsRef.current = true;
      fetchAgencyClinics();
    }
  }, [agencyClinics.length, agencyClinicsLoading, fetchAgencyClinics]);

  // Check if user has permission to access Intake
  if (!hasPermission('intake:access')) {
    return (
      <Alert className='border-red-200 bg-red-50'>
        <AlertDescription className='text-red-800 flex gap-1 items-center'>
          <AlertTriangle className='h-4 w-4 text-red-600' />
          You don't have permission to access Intake Manager features. Please
          contact your administrator if you need access.
        </AlertDescription>
      </Alert>
    );
  }

  // Load intakes when filters change
  const fetchIntakes = useCallback(async () => {
    const hasAssigneeFilter =
      assigneeFilter.includeUnassigned ||
      (assigneeFilter.userIds?.length ?? 0) > 0;
    const filters: IntakeFilters = {
      search: debouncedSearchTerm || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      statuses: statusFilters,
      clinicId: clinicFilter !== 'all' ? clinicFilter : undefined,
      assigneeIds:
        hasAssigneeFilter && (assigneeFilter.userIds?.length ?? 0) > 0
          ? assigneeFilter.userIds
          : undefined,
      assigneeIncludeUnassigned: hasAssigneeFilter
        ? assigneeFilter.includeUnassigned
        : undefined,
      page: currentPage,
      perPage: DEFAULT_PER_PAGE,
      sortBy,
      sortOrder,
    };

    const result = await loadIntakes(filters);
    setPaginationInfo(result);
  }, [
    loadIntakes,
    debouncedSearchTerm,
    startDate,
    endDate,
    statusFilters,
    assigneeFilter,
    clinicFilter,
    currentPage,
    sortBy,
    sortOrder,
  ]);

  // Initial load and when filters change
  useEffect(() => {
    fetchIntakes();
  }, [fetchIntakes]);

  // Reset to page 1 when search/filter changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setCurrentPage(1);
    setSelectedIntakeIds([]);
  }, [
    debouncedSearchTerm,
    statusFilters,
    assigneeFilter,
    clinicFilter,
    startDate,
    endDate,
  ]);

  // Reset all filters to default
  const handleResetFilters = useCallback(() => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setStatusFilters(['active']);
    setAssigneeFilter({ userIds: [], includeUnassigned: false });
    setClinicFilter('all');
    const defaultDateRange = getDefaultIntakeDateRange();
    setStartDate(defaultDateRange.startDate);
    setEndDate(defaultDateRange.endDate);
    setSortBy('created_at');
    setSortOrder('desc');
    setCurrentPage(1);
    setSelectedIntakeIds([]);
  }, []);

  // Empty-state shortcut to the Status control's own "Show All" item. It goes
  // through setStatusFilters — the very function IntakeStatusFilter's onChange
  // is bound to — so the two entry points are the same code path and cannot
  // drift. It must change nothing else: the dates, search, assignee and clinic
  // the clinician set are theirs to keep. Page reset is not done here for the
  // same reason: the dropdown does not do it either, and the shared
  // filter-change effect below already resets to page 1 for both.
  const handleShowAllIntakes = useCallback(() => {
    setStatusFilters(['include_all']);
  }, []);

  // The other half of the empty state: the date window is the most common
  // reason an intake is missing, and the seeded default window is itself what
  // hides anything older than six weeks. Clears only the dates — the search
  // term, status, assignee and clinic stay. Deliberately NOT Reset Filters,
  // which would restore the six-week window and discard the search term,
  // leaving an old intake just as hidden. No page reset, per the note above.
  const handleClearDates = useCallback(() => {
    setStartDate('');
    setEndDate('');
  }, []);

  // Pagination handlers
  const handlePreviousPage = useCallback(() => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPage(prev => Math.min(paginationInfo.totalPages, prev + 1));
  }, [paginationInfo.totalPages]);

  const getPatientName = (intake: IntakeRecord) => {
    const firstName = intake.patient_first_name || '';
    const lastName = intake.patient_last_name || '';
    return firstName || lastName ? `${firstName} ${lastName}`.trim() : 'N/A';
  };

  const isProcessing = (intake: IntakeRecord) => {
    // Don't show as processing if already processed or has errors
    if (intake.is_processed || intake.processing_errors) {
      return false;
    }

    // Check if intake is in "received" status and missing key data
    if (intake.status !== 'received') {
      return false;
    }

    // If explicitly marked as processing, show as processing
    if (intake.processing_status === 'processing') {
      return true;
    }

    // Check if key patient information is missing (Provider Name is optional per extraction tab)
    const hasPatientName =
      intake.patient_first_name || intake.patient_last_name;
    const hasInsurance = !!intake.agency_insurance;

    // If patient name or insurance is missing, consider it still processing
    return !hasPatientName || !hasInsurance;
  };

  const isFailed = (intake: IntakeRecord) => {
    return intake.processing_status === 'failed' && !!intake.processing_errors;
  };

  const handleNewIntakeCreated = () => {
    // Close the dialog immediately
    setShowNewIntakeDialog(false);
    // Refresh intakes to show the newly uploaded intake
    fetchIntakes();
    // No need to call pollIntakeStatus - IntakeRowWithStatus will handle workflow polling
  };

  const handleIntakeWorkflowComplete = async (intakeId: string) => {
    // Refresh only this specific intake instead of reloading all intakes
    console.log('[IntakeManager] Refreshing single intake:', intakeId);
    try {
      const updatedIntake = await loadIntakeDetails(intakeId);
      // Update the intake in the intakes array
      updateIntakeInStore(intakeId, updatedIntake);
    } catch (error) {
      console.error('[IntakeManager] Failed to refresh intake:', error);
    }
  };

  // Rerun handlers
  const handleRerunClick = (intake: IntakeRecord) => {
    setIntakeToRerun(intake);
    setRerunModalOpen(true);
  };

  const handleRerunSuccess = () => {
    setIntakeToRerun(null);
    // Close the drawer if it was open
    setFailedDrawerOpen(false);
    setSelectedFailedIntake(null);
    // Refresh intakes list
    fetchIntakes();
  };

  // Failed intake drawer handlers
  const handleFailedIntakeClick = (intake: IntakeRecord) => {
    setSelectedFailedIntake(intake);
    setFailedDrawerOpen(true);
  };

  const handleRetryFromDrawer = () => {
    if (selectedFailedIntake) {
      handleRerunClick(selectedFailedIntake);
    }
  };

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIntakeIds(intakes.map(intake => intake.id));
    } else {
      setSelectedIntakeIds([]);
    }
  };

  const handleSelectIntake = (intakeId: string, checked: boolean) => {
    if (checked) {
      setSelectedIntakeIds(prev => [...prev, intakeId]);
    } else {
      setSelectedIntakeIds(prev => prev.filter(id => id !== intakeId));
    }
  };

  const handleClearSelection = () => {
    setSelectedIntakeIds([]);
  };

  const handleRemoveFromSelection = (ids: string[]) => {
    setSelectedIntakeIds(prev => prev.filter(id => !ids.includes(id)));
  };

  const handleExportToExcel = useCallback(async () => {
    setIsExporting(true);
    try {
      const hasAssigneeFilter =
        assigneeFilter.includeUnassigned ||
        (assigneeFilter.userIds?.length ?? 0) > 0;
      await downloadIntakeExport({
        search: debouncedSearchTerm || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        statuses: statusFilters,
        clinicId: clinicFilter !== 'all' ? clinicFilter : undefined,
        assigneeIds:
          hasAssigneeFilter && (assigneeFilter.userIds?.length ?? 0) > 0
            ? assigneeFilter.userIds
            : undefined,
        assigneeIncludeUnassigned: hasAssigneeFilter
          ? assigneeFilter.includeUnassigned
          : undefined,
        sortBy,
        sortOrder,
      });
    } catch (error) {
      console.error('Error exporting intakes to Excel:', error);
      // The row-limit refusal is a 4xx business message the user must see to
      // know how to fix it; anything else falls back to generic copy.
      showError(
        await getUserFacingApiMessageAsync(
          error,
          'Failed to export intakes. Please try again.'
        )
      );
    } finally {
      setIsExporting(false);
    }
  }, [
    debouncedSearchTerm,
    startDate,
    endDate,
    statusFilters,
    clinicFilter,
    assigneeFilter,
    sortBy,
    sortOrder,
  ]);

  const hasAssigneeSelection =
    assigneeFilter.includeUnassigned ||
    (assigneeFilter.userIds?.length ?? 0) > 0;

  // Could anything the user set explain the empty list? If not, the agency
  // simply has no intakes yet and the right message is "upload a document",
  // with neither link — they would reveal nothing and only make the page look
  // broken. Both links hang off this one boolean to keep that guarantee.
  const hasAdjustedSearchOrFilters =
    !!searchTerm ||
    statusFilters.length !== 1 ||
    statusFilters[0] !== 'active' ||
    hasAssigneeSelection ||
    clinicFilter !== 'all' ||
    startDate !== defaultDateRange.startDate ||
    endDate !== defaultDateRange.endDate;

  // Would each link change anything? Each is gated on its own lever only.
  const canShowAllStatuses = !isIncludeAllSelection(statusFilters);
  const canClearDates = !!startDate || !!endDate;

  const handleSort = (column: IntakeSortColumn) => {
    if (sortBy === column) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const SortableHeader: React.FC<{
    column: IntakeSortColumn;
    label: string;
  }> = ({ column, label }) => (
    <TableHead>
      <button
        type='button'
        className='flex items-center hover:text-foreground transition-colors text-left font-medium'
        onClick={() => handleSort(column)}
      >
        {label}
        {sortBy === column ? (
          sortOrder === 'asc' ? (
            <ArrowUp className='h-4 w-4 ml-1' />
          ) : (
            <ArrowDown className='h-4 w-4 ml-1' />
          )
        ) : null}
      </button>
    </TableHead>
  );

  return (
    <div className='space-y-6'>
      {/* Header and New Intake Button — on mobile the title gets its own row and
          the actions wrap onto the row below so the primary "New Intake" CTA
          stays visible instead of overflowing off-screen. */}
      <div className='flex flex-col gap-3 md:flex-row md:justify-between md:items-center'>
        <div>
          <h1 className='text-2xl font-bold tracking-tight'>Intake Manager</h1>
        </div>
        <div className='flex flex-wrap gap-2'>
          {canAccessAdminIntakeActions && (
            <>
              <Button
                variant='outline'
                onClick={handleExportToExcel}
                disabled={isExporting}
              >
                {isExporting ? (
                  <Loader2 className='h-4 w-4 animate-spin' />
                ) : (
                  <FileSpreadsheet className='h-4 w-4' />
                )}
                Export to Excel
              </Button>
              <Button
                type='button'
                variant='outline'
                onClick={() => setColumnsEditorOpen(true)}
              >
                Customize columns
              </Button>
            </>
          )}
          <Button onClick={() => setShowNewIntakeDialog(true)}>
            <Plus className='h-4 w-4' />
            New Intake
          </Button>
        </div>
      </div>

      {/* Error Display */}
      {intakesError && (
        <Card className='border-destructive bg-destructive/10'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2 text-destructive'>
              <AlertCircle className='h-4 w-4' />
              <span className='font-medium'>Error loading intakes</span>
            </div>
            <p className='text-sm text-destructive/80 mt-1'>{intakesError}</p>
            <Button
              variant='outline'
              size='sm'
              className='mt-2'
              onClick={() => fetchIntakes()}
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Filter Controls — a compact 2-column grid on mobile so the fields pack
          two-per-row (Search spans the full width) instead of each taking a full
          row; desktop keeps the original single flex-wrap row. */}
      <div className='grid grid-cols-2 gap-3 items-end md:flex md:flex-wrap md:gap-4'>
        {/* Search */}
        <div className='col-span-2 md:flex-1 md:min-w-[200px]'>
          <label htmlFor='search' className='text-sm font-medium mb-1.5 block'>
            Search
          </label>
          <Input
            id='search'
            placeholder='Search across all column data (patient, referral, insurance, allergies, notes, assignee, …)'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Start Date */}
        <div className='w-full md:w-40'>
          <label
            htmlFor='startDate'
            className='text-sm font-medium mb-1.5 block'
          >
            Start Date
          </label>
          <DatePicker
            id='startDate'
            className='w-full'
            typable
            showClearButton
            clearButtonLabel='Clear start date'
            date={parseISOToLocalDate(startDate)}
            onDateChange={d => setStartDate(d ? formatDateToISO(d) : '')}
          />
        </div>

        {/* End Date */}
        <div className='w-full md:w-40'>
          <label htmlFor='endDate' className='text-sm font-medium mb-1.5 block'>
            End Date
          </label>
          <DatePicker
            id='endDate'
            className='w-full'
            typable
            showClearButton
            clearButtonLabel='Clear end date'
            date={parseISOToLocalDate(endDate)}
            onDateChange={d => setEndDate(d ? formatDateToISO(d) : '')}
          />
        </div>

        {/* Status Filter */}
        <div className='w-full md:w-56'>
          <label htmlFor='status' className='text-sm font-medium mb-1.5 block'>
            Status
          </label>
          <IntakeStatusFilter
            value={statusFilters}
            onChange={setStatusFilters}
            enabledTabs={enabledTabs}
          />
        </div>

        {/* Assignee Filter - min-w-0 allows flex shrink so button truncates and doesn't overflow into Reset */}
        <div className='w-full min-w-0 md:w-56'>
          <label
            htmlFor='assignee'
            className='text-sm font-medium mb-1.5 block'
          >
            Assignee
          </label>
          <IntakeAssigneeFilter
            value={assigneeFilter}
            onChange={setAssigneeFilter}
          />
        </div>

        {/* Clinic Filter - only show when there are multiple clinics */}
        {agencyClinics.length > 1 && (
          <div className='w-full md:w-48'>
            <ClinicSelect
              label='Clinic'
              clinics={agencyClinics}
              includeAllOption
              allOptionValue='all'
              value={clinicFilter}
              onChange={e => setClinicFilter(e.target.value)}
              placeholder='Filter by clinic'
            />
          </div>
        )}

        {/* Reset Button */}
        <Button
          variant='outline'
          onClick={handleResetFilters}
          className='w-full justify-center md:w-auto flex items-center gap-2'
        >
          <RotateCcw className='h-4 w-4' />
          Reset
        </Button>
      </div>

      {intakesLoading ? (
        <div className='flex items-center justify-center py-16'>
          <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
          <span className='ml-3 text-muted-foreground'>Loading intakes...</span>
        </div>
      ) : intakes.length === 0 ? (
        <div className='text-center text-muted-foreground py-8'>
          <FileText className='h-12 w-12 mx-auto mb-4 text-gray-500' />
          <p>No intake records found</p>
          <p className='text-sm'>
            {hasAdjustedSearchOrFilters
              ? 'Try adjusting your search or filters'
              : 'Upload a document to get started'}
          </p>
          {hasAdjustedSearchOrFilters &&
            (canShowAllStatuses || canClearDates) && (
              // size='sm' resolves to display:flex, so the parent's text-center
              // cannot center these as inline content; the row does it instead
              // and wraps rather than overflowing on a narrow screen.
              <div className='mt-1 flex flex-wrap items-center justify-center gap-x-3'>
                {canShowAllStatuses && (
                  <Button
                    variant='link'
                    size='sm'
                    onClick={handleShowAllIntakes}
                  >
                    {/* Deliberately wordier than the menu item it triggers
                        ("Show All"): standing alone under the hint it has to
                        name what it widens. The Status control reads "Show
                        All" after the click, showing where the setting
                        lives. */}
                    Show All Statuses
                  </Button>
                )}
                {canClearDates && (
                  <Button variant='link' size='sm' onClick={handleClearDates}>
                    Clear dates
                  </Button>
                )}
              </div>
            )}
        </div>
      ) : (
        <div className='space-y-4'>
          {/* Processing Summary */}
          {intakes.some(intake => isProcessing(intake)) && (
            <div className='flex items-center gap-2 text-sm text-blue-600 bg-blue-50 p-3 rounded-md'>
              <Loader2 className='h-4 w-4 animate-spin' />
              <span>
                {intakes.filter(intake => isProcessing(intake)).length}{' '}
                intake(s) currently processing
              </span>
            </div>
          )}

          <div className='rounded-md border'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className='w-12'>
                    <Checkbox
                      checked={
                        intakes.length > 0 &&
                        selectedIntakeIds.length === intakes.length
                      }
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  {visibleColumns.map(colId => {
                    const sk = sortKeyForIntakeManagerColumn(colId);
                    const label = labelForIntakeManagerColumn(colId);
                    if (sk) {
                      return (
                        <SortableHeader key={colId} column={sk} label={label} />
                      );
                    }
                    return <TableHead key={colId}>{label}</TableHead>;
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {intakes.map(intake => (
                  <IntakeRowWithStatus
                    key={intake.id}
                    intake={intake}
                    visibleColumns={visibleColumns}
                    isProcessing={isProcessing(intake)}
                    getPatientName={getPatientName}
                    agencyClinics={agencyClinics}
                    agencyName={user?.agency?.written_name}
                    onClick={e => {
                      // Don't navigate if processing
                      if (isProcessing(intake)) {
                        return;
                      }
                      // Open drawer for failed intakes instead of navigating
                      if (isFailed(intake)) {
                        handleFailedIntakeClick(intake);
                        return;
                      }
                      navigateTo(`/intake/${intake.id}`)(e);
                    }}
                    onWorkflowComplete={handleIntakeWorkflowComplete}
                    onAssignComplete={() =>
                      handleIntakeWorkflowComplete(intake.id)
                    }
                    enabledTabs={enabledTabs}
                    onRerun={handleRerunClick}
                    showCheckbox={true}
                    isSelected={selectedIntakeIds.includes(intake.id)}
                    onCheckboxChange={checked =>
                      handleSelectIntake(intake.id, checked)
                    }
                  />
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination Controls */}
          <div className='flex items-center justify-between border-t pt-4'>
            <div className='text-sm text-muted-foreground'>
              Showing {(currentPage - 1) * DEFAULT_PER_PAGE + 1} to{' '}
              {Math.min(currentPage * DEFAULT_PER_PAGE, paginationInfo.total)}{' '}
              of {paginationInfo.total} intakes
            </div>
            <div className='flex items-center gap-2'>
              <Button
                variant='outline'
                size='sm'
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
              >
                <ChevronLeft className='h-4 w-4' />
                Previous
              </Button>
              <div className='text-sm px-2'>
                Page {currentPage} of {paginationInfo.totalPages}
              </div>
              <Button
                variant='outline'
                size='sm'
                onClick={handleNextPage}
                disabled={currentPage >= paginationInfo.totalPages}
              >
                Next
                <ChevronRight className='h-4 w-4' />
              </Button>
            </div>
          </div>
        </div>
      )}

      <IntakeManagerColumnsEditorDialog
        open={columnsEditorOpen}
        onOpenChange={setColumnsEditorOpen}
        storedColumnIds={intakeManagerStoredColumns}
        onAfterSave={refreshIntakeManagerColumns}
      />

      {/* New Intake Dialog */}
      <NewIntakeDialog
        open={showNewIntakeDialog}
        onOpenChange={setShowNewIntakeDialog}
        onIntakeCreated={handleNewIntakeCreated}
      />

      {/* Rerun Intake Dialog */}
      <RerunIntakeDialog
        open={rerunModalOpen}
        onOpenChange={setRerunModalOpen}
        intake={intakeToRerun}
        onSuccess={handleRerunSuccess}
        getPatientName={getPatientName}
      />

      {/* Failed Intake Drawer */}
      <FailedIntakeDrawer
        open={failedDrawerOpen}
        onOpenChange={setFailedDrawerOpen}
        intake={selectedFailedIntake}
        onRetry={handleRetryFromDrawer}
      />

      {/* Selection Actions Bar */}
      {selectedIntakeIds.length > 0 && (
        <IntakeSelectionActions
          selectedIntakes={intakes.filter(intake =>
            selectedIntakeIds.includes(intake.id)
          )}
          onClearSelection={handleClearSelection}
          onRemoveFromSelection={handleRemoveFromSelection}
          onAssignComplete={fetchIntakes}
        />
      )}
    </div>
  );
};

export default IntakeManager;
